import { Drawer } from "@mui/material";
import { BrandKit } from "@redotech/redo-model/brand-kit";
import {
  DesktopStepConfig,
  DesktopStepType,
  MobileStepConfig,
  MobileStepType,
  SpotlightForm,
} from "@redotech/redo-model/spotlight-schema";
import {
  getContainerStyles,
  SpotlightContainerStyles,
} from "@redotech/redo-model/spotlight-styles";
import { memo } from "react";
import {
  MerchantCustomizableModal,
  ModalPlacement,
} from "../../storefront/merchant-customizable-modal";
import { ANONYMOUS_SHOPPER_ID_KEY } from "../../utils/shared-conf";
import { CustomFieldProvider } from "../form-elements/context";
import { SpotlightFormContent } from "./spotlight-form-content";
import { SpotlightClient } from "./types";

export interface SpotlightModalProps {
  ariaLabel?: string;
  spotlight: SpotlightForm;
  open: boolean;
  completed: boolean;
  currentStep: number;
  onClose: () => void;
  client: SpotlightClient | undefined;
  brandKit?: BrandKit;
  isMobile: boolean;
  mobileConfig?: MobileStepConfig;
  desktopConfig?: DesktopStepConfig;
  merchantPhoneNumber?: string;
  onSubmitStep: (i: number, formData: Record<string, string>) => Promise<void>;
  resolvedDiscounts?: Record<string, string>;
  onCheckoutAction?: () => Promise<void>;
  buttonSubtext?: string;
}

export const SpotlightModal = memo(function SpotlightModal({
  ariaLabel,
  spotlight,
  open,
  completed,
  currentStep,
  onClose,
  client,
  brandKit,
  isMobile,
  mobileConfig,
  desktopConfig,
  merchantPhoneNumber,
  onSubmitStep,
  resolvedDiscounts,
  onCheckoutAction,
  buttonSubtext,
}: SpotlightModalProps) {
  const config = isMobile ? mobileConfig : desktopConfig;

  if (!config) {
    return null;
  }

  const isFullscreen =
    (isMobile && mobileConfig?.type === MobileStepType.FULLSCREEN) ||
    (!isMobile && desktopConfig?.type === DesktopStepType.FULLSCREEN);

  const isDrawer = isMobile && mobileConfig?.type === MobileStepType.DRAWER;
  const isPanel = !isMobile && desktopConfig?.type === DesktopStepType.PANEL;

  const content = (
    <CustomFieldProvider
      getCustomFieldOptions={(id) =>
        client?.getCustomFieldOptions({ customFieldId: id }) ??
        Promise.resolve({ name: "", options: [] })
      }
    >
      <SpotlightFormContent
        anonymousId={
          localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY) ?? undefined
        }
        brandKit={brandKit}
        buttonSubtext={buttonSubtext}
        config={config}
        currentStep={currentStep}
        isMobile={isMobile}
        merchantPhoneNumber={merchantPhoneNumber}
        onCheckoutAction={onCheckoutAction}
        onClose={onClose}
        onSubmitStep={onSubmitStep}
        resolvedDiscounts={resolvedDiscounts}
        spotlight={spotlight}
      />
    </CustomFieldProvider>
  );

  // Get overlay backdrop color from config
  const backdropColor =
    "overlay" in config.advancedSettings && config.advancedSettings.overlay
      ? config.advancedSettings.overlay
      : "rgba(0, 0, 0, 0.5)";

  // Drawer for mobile drawer type - always anchored to bottom
  if (isDrawer && mobileConfig?.type === MobileStepType.DRAWER) {
    const containerStyles = getContainerStyles(
      mobileConfig,
      true,
      window.innerHeight,
    );
    return (
      <Drawer
        anchor="bottom"
        onClose={onClose}
        open={open && !completed}
        PaperProps={{ style: containerStylesToCSS(containerStyles) }}
        slotProps={{ backdrop: { style: { backgroundColor: backdropColor } } }}
      >
        {content}
      </Drawer>
    );
  }

  // Panel for desktop panel type
  if (isPanel && desktopConfig?.type === DesktopStepType.PANEL) {
    const anchor = getDrawerAnchor(desktopConfig.placement);
    const containerStyles = getContainerStyles(
      desktopConfig,
      false,
      window.innerHeight,
    );
    return (
      <Drawer
        anchor={anchor}
        onClose={onClose}
        open={open && !completed}
        PaperProps={{ style: containerStylesToCSS(containerStyles) }}
        slotProps={{ backdrop: { style: { backgroundColor: backdropColor } } }}
      >
        {content}
      </Drawer>
    );
  }

  // Dialog for popup and fullscreen

  const placement: ModalPlacement =
    !isMobile && desktopConfig?.type === DesktopStepType.POPUP
      ? (desktopConfig.placement as ModalPlacement)
      : "center";

  // Don't pass containerStyle for fullscreen - let MUI Dialog handle it natively
  const containerStyles = isFullscreen
    ? undefined
    : containerStylesToCSS(
        getContainerStyles(config, isMobile, window.innerHeight),
      );

  return (
    <MerchantCustomizableModal
      ariaLabel={ariaLabel}
      backdropColor={backdropColor}
      containerStyle={containerStyles}
      fullscreen={isFullscreen}
      onClose={onClose}
      open={open && !completed}
      placement={placement}
    >
      {content}
    </MerchantCustomizableModal>
  );
});

function getDrawerAnchor(
  placement: string,
): "left" | "right" | "top" | "bottom" {
  switch (placement) {
    case "left":
      return "left";
    case "right":
      return "right";
    case "top":
      return "top";
    case "bottom":
      return "bottom";
    default:
      return "bottom";
  }
}

function containerStylesToCSS(
  styles: SpotlightContainerStyles,
): React.CSSProperties {
  return {
    width: styles.width,
    height: styles.height,
    minHeight: styles.minHeight,
    maxHeight: styles.maxHeight,
    borderRadius: styles.borderRadius,
    borderWidth: styles.borderWidth,
    borderColor: styles.borderColor,
    borderStyle: styles.borderWidth ? "solid" : undefined,
    backgroundColor: styles.backgroundColor,
  };
}
